import { defineComponent as _defineComponent } from 'vue'
import BaseDialog from "@/components/base-dialog.vue";
import { useShowWarningMessage } from "@/composables/use-notifications";
import { Ref, ref } from "vue";
import PreviewOnlineAnswerSheetDialog from "./preview-online-answer-sheet-dialog.vue";
import { Editor } from "tinymce";
import useHttpRequest from "@/composables/use-http-request";
import AwsUploadUrl from "@/interfaces/awsUploadUrl.interface";
import { useUploadResourceToAWS } from "@/composables/use-upload-resource-to-aws";
import { Loading } from "element-ui";
import PreviewFileUploadAnswerSheetDialog from "./preview-file-upload-answer-sheet-dialog.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'submit-answer-sheet-dialog',
  props: {
    isVisible: { type: Boolean },
    submissionType: null,
    textEditorInstance: null,
    assessmentType: null,
    assessmentId: null
  },
  emits: {
  "on-close": () => true,
  "on-submit-online-answer-sheet-intent": () => true,
  "on-submit-file-upload-answer-sheet-intent": (path: {
    url: string;
    key: string;
    file_name: string;
  }) => path,
},
  setup(__props: any, { emit }) {

const props = __props as {
  isVisible: boolean;
  submissionType?: "Online" | "File Upload";
  textEditorInstance?: Editor;
  assessmentType: "Examination" | "Course Work";
  assessmentId: string;
}



const consentAgreed = ref(false);
const selectedFile: Ref<File | undefined> = ref();
const previewFileUrl = ref("");
const previewFileKey = ref("");
const fileName = ref("");



const onClose = () => {
  consentAgreed.value = false;
  selectedFile.value = undefined;
  emit("on-close");
};

const onlineAnswerSheetPreviewDialogVisible = ref(false);

const onSubmitOnlineAnswerSheetIntent = () => {
  onlineAnswerSheetPreviewDialogVisible.value = false;
  emit("on-submit-online-answer-sheet-intent");
};

const fileUploadAnswerSheetPreviewDialogVisible = ref(false);

const onSubmitFileUploadAnswersheetIntent = () => {
  fileUploadAnswerSheetPreviewDialogVisible.value = false;
  emit("on-submit-file-upload-answer-sheet-intent", {
    url: previewFileUrl.value,
    key: previewFileKey.value,
    file_name: fileName.value,
  });
};

const requestFileUploadAnswerSheetUploadUrl = async () => {
  const target =
    props.assessmentType === "Examination" ? "examinations" : "course-work";
  const idFieldname =
    props.assessmentType === "Examination"
      ? "examination_id"
      : "course_work_id";

  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    answer_sheet: { upload_url: AwsUploadUrl; path_key: string };
  }>({
    method: "POST",
    url: `${target}/request-for-answersheet-upload-url`,
    body: {
      [idFieldname]: props.assessmentId,
      answer_sheet: {
        file_name: selectedFile.value?.name,
        file_size: selectedFile.value?.size,
      },
    },
    loadingProperty: null,
    showSuccessMessage: false,
    errorLoadingProperty: null,
  });

  if (!request) {
    return;
  }

  if (
    request.success &&
    request.message == "ANSWER SHEET UPLOAD URL GENERATED"
  ) {
    return request.answer_sheet;
  }
};

const onPreviewAnswerSheet = async () => {
  let loadingIndicator: ReturnType<typeof Loading.service> | undefined;
  try {
    if (!consentAgreed.value) {
      return useShowWarningMessage(
        "Consent Required.",
        "In order to submit your answersheet, please agree to the No Plagiarism disclaimer by checking the box above."
      );
    }

    if (props.submissionType === "Online") {
      onlineAnswerSheetPreviewDialogVisible.value = true;

      //Close the current dialog
      onClose();
    } else if (props.submissionType === "File Upload") {
      if (!selectedFile.value) {
        return useShowWarningMessage(
          "No File Selected",
          "Please select a file to upload before proceeding."
        );
      }

      if (selectedFile.value.size === 0) {
        return useShowWarningMessage(
          "Invalid File Size",
          "The file you are trying to upload is 0KB. Please ensure you're uploading the the correct file."
        );
      }

      loadingIndicator = Loading.service({
        lock: true,
        text: "Please wait...",
        background: "rgba(0, 0, 0, 0.7)",
      });

      const uploadUrl = await requestFileUploadAnswerSheetUploadUrl();

      if (!uploadUrl) {
        return;
      }

      const uploadedFileUrl = await useUploadResourceToAWS({
        awsUploadUrl: uploadUrl.upload_url,
        selectedFile: selectedFile.value,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onProgress: (progressEvent) => {
          const loader = document.querySelector(
            ".el-loading-text"
          ) as HTMLElement;

          const progress = +(
            (progressEvent.loaded / progressEvent.total) *
            100
          ).toFixed(0);

          loader.innerText = `Preparing Preview... ${progress}%`;
        },
      });

      if (!uploadedFileUrl) {
        return;
      }

      previewFileUrl.value = uploadedFileUrl;
      previewFileKey.value = uploadUrl.path_key;
      fileName.value = selectedFile.value.name;
      fileUploadAnswerSheetPreviewDialogVisible.value = true;

      onClose();
    }
  } catch (error) {
    useShowWarningMessage("Error", "An error occurred while uploading files");
  } finally {
    loadingIndicator?.close();
  }
};

const onSelectFile = (e: any) => {
  e.preventDefault();

  if (!e.target.files?.length) {
    return;
  }

  selectedFile.value = e.target.files[0];
};

const deleteSelectedFile = () => {
  selectedFile.value = undefined;
};

return { __sfc: true,props, consentAgreed, selectedFile, previewFileUrl, previewFileKey, fileName, emit, onClose, onlineAnswerSheetPreviewDialogVisible, onSubmitOnlineAnswerSheetIntent, fileUploadAnswerSheetPreviewDialogVisible, onSubmitFileUploadAnswersheetIntent, requestFileUploadAnswerSheetUploadUrl, onPreviewAnswerSheet, onSelectFile, deleteSelectedFile, BaseDialog, PreviewOnlineAnswerSheetDialog, PreviewFileUploadAnswerSheetDialog }
}

})